import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import { useActiveInlineCommentsQuery } from '@confluence/comments-data/entry-points/useActiveInlineCommentsQuery';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useIsNewDanglingCommentExperienceEnabled } from '@confluence/comments-panel-utils/entry-points/useIsNewDanglingCommentExperienceEnabled';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	LocationValue,
	getAnnotationThreadKeys,
	getAnnotationsToLoad,
	getOpenCommentThreads,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { usePanelRefresh } from '../hooks/usePanelRefresh';
import { SIDEBAR_COMMENTS_PANEL_LOAD_OPEN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';

import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { CommentsPanelList } from '../components/CommentsPanelList';
import { EmptyOpenViewSVG } from '../assets/EmptyOpenView';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_OPEN_VIEW_METRIC } from '../perf.config';

import {
	DEFAULT_TOP_LEVEL_ACTIONS,
	DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE,
} from './topLevelActions';

type OpenViewProps = {
	showDeleteOption: boolean;
	flags?: FlagsStateContainer;
	// Disable loading UI for VR tests
	disabledLoading?: boolean;
};

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.empty.open.comments.header.text',
		defaultMessage: 'Be the first to comment',
		description:
			'Header text to display in Open View empty state when there are no open comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.empty.open.comments.body.text',
		defaultMessage:
			'Highlight some text and select {highlighted} or add a comment at the bottom of this panel.',
		description:
			'Body text to display in Open View empty state when there are no Open comments on the page.',
	},
	emptyCommentsBodyTextForDraft: {
		id: 'comments-panel.empty.open.comments.body.text.draft',
		defaultMessage: 'Highlight some text and select {highlighted}.',
		description:
			'Body text to display in Open View empty state when there are no Open comments on the draft page.',
	},
	emptyCommentsBodyOnArchivedPage: {
		id: 'comments-panel.empty.open.comments.body.text.archived',
		defaultMessage: 'Restore this content to add comments to it.',
		description:
			'Body text to display in Open View empty state when there are no Open comments on the archived page.',
	},
	emptyWhiteboardCommentsBodyText: {
		id: 'comments-panel.empty.open.whiteboard.comments.header.text',
		defaultMessage:
			'Select Comments from the toolbar and click anywhere on this whiteboard to add a comment.',
		description:
			'Body text to display in Open View empty state when there are no Open comments on the whiteboard.',
	},
	emptyDatabaseCommentsBodyText: {
		id: 'comments-panel.empty.open.database.comments.header.text',
		defaultMessage: 'Select the comment button from any value or right-click and select Comment.',
		description:
			'Body text to display in Open View empty state when there are no Open comments on the database.',
	},
});

// TODO: Update this to use page comments when they are supported
export const OpenView = ({ showDeleteOption, flags, disabledLoading = false }: OpenViewProps) => {
	const [
		{ orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap },
		{ clearRemovedComments, clearCommentsData },
	] = useCommentsData();
	const [
		{ initialDataLoadedForViewMap, showInlineComments, showGeneralComments },
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.OPEN];

	const { commentService } = useCommentService();
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const annotationsToLoad = getAnnotationsToLoad(orderedActiveAnnotationIdList);
	const pageMode = useGetPageMode();
	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);
	const isNewDanglingCommentExperienceEnabled = useIsNewDanglingCommentExperienceEnabled();

	const { contentStatus } = useContentStatus();

	const commentsQueryHook = fg('confluence_frontend_comments_panel_v2')
		? useActiveCommentsQuery
		: useActiveInlineCommentsQuery;
	const commentsQueryHookVariables: any = fg('confluence_frontend_comments_panel_v2')
		? {
				pageId: contentId || '',
			}
		: {
				pageId: contentId || '',
				markerRefList: annotationsToLoad,
				// skip entirely if no unresolved comments on the page
				skip: annotationsToLoad.length === 0,
			};

	const { loading, error, refetch, data } = commentsQueryHook(commentsQueryHookVariables);
	const { shouldSuppressEmptyState, shouldShowLoading } = usePanelRefresh({
		refetch,
		clearCommentsData,
		registerTriggerCommentPanelRefresh: (handler) =>
			commentService.registerTriggerCommentPanelRefresh(handler),
		queryLoading: loading,
		disabled: disabledLoading,
	});

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	useEffect(() => {
		if (!loading) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.OPEN });
		}
	}, [loading, setInitialDataLoadedForView]);

	const formattedText = useMemo(() => {
		if (contentType === 'whiteboard') {
			return i18n.emptyWhiteboardCommentsBodyText;
		} else if (contentType === 'database') {
			return i18n.emptyDatabaseCommentsBodyText;
		} else if (contentStatus === ContentStatus.DRAFT) {
			return i18n.emptyCommentsBodyTextForDraft;
		} else if (contentStatus === ContentStatus.ARCHIVED) {
			return i18n.emptyCommentsBodyOnArchivedPage;
		} else {
			return i18n.emptyCommentsBodyText;
		}
	}, [contentStatus, contentType]);

	const commentThreads = useMemo(() => {
		if (
			(!isInitialCommentDataLoaded && loading) ||
			(orderedActiveAnnotationIdList.length === 0 && Object.keys(removedThreadsMap).length === 0)
		) {
			return [];
		}

		return fg('confluence_frontend_content_wrapper')
			? getVisibleCommentThreads({
					commentsDataMap,
					includeGeneralComments: showGeneralComments,
					includeInlineComments: showInlineComments,
					orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					isNewDanglingCommentExperienceEnabled,
				})
			: getOpenCommentThreads({
					commentsDataMap,
					showInlineComments,
					showGeneralComments,
					location: LocationValue.PANEL,
					orderedActiveAnnotationIdList,
					removedThreadsMap,
				});
	}, [
		isInitialCommentDataLoaded,
		loading,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		commentsDataMap,
		showGeneralComments,
		showInlineComments,
		isNewDanglingCommentExperienceEnabled,
	]);

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: fg('confluence_frontend_abort_panel_exp_if_no_comment')
			? commentThreads.length
			: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_OPEN_EXPERIENCE,
		metric: COMMENTS_PANEL_OPEN_VIEW_METRIC,
	});

	if (shouldShowLoading(isInitialCommentDataLoaded, loading)) {
		return <Loading />;
	}

	if (
		error &&
		(!onlyHasReactionsSummaryErrors(error) || !fg('confluence_frontend_handle_reactions_errors'))
	) {
		return (
			<ErrorPanel
				error={error}
				onRetryClick={async () => {
					await refetch();
				}}
			/>
		);
	}

	if (commentThreads.length === 0 && !shouldSuppressEmptyState()) {
		return (
			<EmptyStateComponent
				SVG={EmptyOpenViewSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(formattedText, { highlighted: <b>Comment</b> })}
				isOpenView
				onRendered={
					fg('confluence_frontend_abort_panel_exp_if_no_comment')
						? () => {
								setCommentBatchRendered(true);
								setAllCommentsRendered(true);
							}
						: undefined
				}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={commentThreads}
			supportedTopLevelActions={
				showDeleteOption ? DEFAULT_TOP_LEVEL_ACTIONS : DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE
			}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
